import { useState } from "react";
import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  RadarChart,
  Radar,
  PolarGrid,
  PolarRadiusAxis,
} from "recharts";
import { Activity, Droplet, HeartPulse, Wind, ArrowUp, HelpCircle, ArrowUpRight } from "lucide-react";
import AnalysesGridView from "./AnalysesGridView";
import VitalSignsGridView from "./VitalSignsGridView";



const radarData = [
  { axis: "Biologique", v: 70 },
  { axis: "Métabolique", v: 55 },
  { axis: "Lipidique", v: 60 },
  { axis: "Hémato.", v: 45 },
  { axis: "Rénal", v: 65 },
  { axis: "Hépatique", v: 50 },
];

const teloData = Array.from({ length: 8 }, (_, i) => ({
  age: 10 + i * 5,
  kb: 5.9 - i * 0.18 - (i > 5 ? 0.3 : 0),
}));

const RISKS = [
  { label: "AVC", value: "17,54", icon: HeartPulse },
  { label: "BPCO", value: "14,45", icon: Wind },
  { label: "Diabète T2", value: "17,32", icon: Droplet },
  { label: "Cancer", value: "4,94", icon: Activity },
  { label: "Cardiopathie", value: "34,21", icon: HeartPulse },
];

export default function LaboratoireView() {
  const [view, setView] = useState<"main" | "analyses" | "vitals">("main");
  if (view === "analyses") return <AnalysesGridView onBack={() => setView("main")} />;
  if (view === "vitals") return <VitalSignsGridView onBack={() => setView("main")} />;
  return (
    <div className="space-y-5">
      {/* Top hero card */}
      <div className="card-surface rounded-3xl p-6 sm:p-8">
        {/* Age strip */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button className="grid h-8 w-8 place-items-center rounded-full bg-white/5 text-muted-foreground">‹</button>
            <h2 className="text-2xl font-light">Rapport d'âge</h2>
            <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs text-accent">Sous-optimal</span>
            <button
              onClick={() => setView("vitals")}
              className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
            >
              Voir détails analyses <ArrowUpRight className="h-3 w-3" />
            </button>
            <button
              onClick={() => setView("analyses")}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-foreground hover:bg-white/10"
            >
              Vue analyses biologiques <ArrowUpRight className="h-3 w-3" />
            </button>

          </div>

          <div className="flex items-center gap-6 rounded-full border border-white/10 bg-white/[0.04] px-5 py-2.5 text-sm">

            <div className="flex items-center gap-2">
              <span className="rounded-md bg-primary/20 px-1.5 py-0.5 text-[10px] text-primary">+5 ans</span>
              <span className="text-muted-foreground">Âge biologique</span>
              <span className="font-medium">47 ans</span>
            </div>
            <span className="h-4 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Âge chronologique</span>
              <span className="font-medium">42 ans</span>
            </div>
            <span className="hidden sm:inline text-xs text-muted-foreground">
              Votre corps vieillit <span className="text-accent">5 ans plus vite</span>
            </span>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr_1fr]">
          {/* Radar */}
          <div>
            <h3 className="text-base font-medium">Âge biologique</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Mesure le vieillissement de votre corps et révèle votre âge biologique réel.
            </p>
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="oklch(1 0 0 / 0.1)" />
                  <PolarAngleAxis dataKey="axis" tick={{ fill: "oklch(0.68 0.01 240)", fontSize: 10 }} />
                  <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                  <Radar dataKey="v" stroke="oklch(0.78 0.18 150)" fill="oklch(0.78 0.18 150 / 0.25)" strokeWidth={1.5} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gauge */}
          <div className="relative">
            <div className="absolute left-1/2 top-2 -translate-x-1/2 grid grid-cols-2 gap-x-12 gap-y-3 text-[11px] text-muted-foreground">
              {RISKS.slice(0, 4).map((r) => {
                const I = r.icon;
                return (
                  <div key={r.label} className="flex items-center gap-1.5">
                    <I className="h-3 w-3 text-primary" />
                    <span className="font-medium text-foreground">{r.value}</span><span>%</span>
                    <span className="ml-1">{r.label}</span>
                  </div>
                );
              })}
            </div>
            <div className="mx-auto h-64 w-full max-w-sm">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  innerRadius="70%"
                  outerRadius="100%"
                  startAngle={200}
                  endAngle={-20}
                  data={[{ v: 74, fill: "oklch(0.65 0.2 250)" }]}
                >
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar background={{ fill: "oklch(1 0 0 / 0.05)" }} dataKey="v" cornerRadius={20} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="pointer-events-none -mt-44 flex flex-col items-center text-center">
                <div className="text-5xl font-light">74<sup className="text-base align-super">e</sup></div>
                <div className="text-xs text-muted-foreground">Percentile</div>
                <div className="mt-12 text-[11px] text-muted-foreground">Votre risque de maladie</div>
              </div>
            </div>
          </div>

          {/* Telomere */}
          <div className="card-surface rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <div className="text-2xl font-light">5,2 <span className="text-sm text-muted-foreground">kb</span></div>
              <span className="rounded-full bg-[oklch(0.85_0.18_85)]/15 px-2 py-0.5 text-[10px] text-[oklch(0.85_0.18_85)]">● Moyen</span>
            </div>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/5">
              <div className="h-full w-2/3 bg-gradient-to-r from-primary to-[oklch(0.85_0.18_85)]" />
            </div>
            <div className="mt-3 text-sm font-medium">Longueur des télomères</div>
            <p className="text-[11px] text-muted-foreground">
              Votre longueur de télomères correspond à celle d'une personne de 50 ans.
            </p>
            <div className="mt-3 h-28">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={teloData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="oklch(1 0 0 / 0.06)" />
                  <XAxis dataKey="age" tick={{ fontSize: 9, fill: "oklch(0.68 0.01 240)" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: "oklch(0.68 0.01 240)" }} axisLine={false} tickLine={false} domain={[4, 6]} />
                  <Line dataKey="kb" stroke="oklch(0.78 0.18 150)" strokeWidth={1.5} dot={{ r: 2, fill: "oklch(0.78 0.18 150)" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <button className="mt-2 w-full rounded-full border border-white/10 bg-white/5 py-1.5 text-xs text-muted-foreground hover:text-foreground">
              Voir le rapport complet ›
            </button>
          </div>
        </div>
      </div>

      {/* Bottom 3 cards */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Methylation */}
        <div className="card-surface rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Score de risque de méthylation</h3>
            <button className="rounded-full bg-white/5 px-3 py-1 text-xs text-muted-foreground">Voir rapport ›</button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Un score de méthylation de <span className="text-foreground">0,76</span> équivaut à un âge biologique de 47, indiquant un vieillissement accéléré.
          </p>
          <div className="mt-4 flex items-end gap-3">
            <div className="text-3xl font-light">0,76</div>
            <div className="text-xs text-muted-foreground">(égal à 47)</div>
            <div className="ml-auto text-xs text-muted-foreground">/ <span className="text-foreground text-base">42</span> Chronologique</div>
          </div>
          <div className="mt-3 h-2 w-full rounded-full bg-gradient-to-r from-primary via-[oklch(0.85_0.18_85)] to-destructive" />
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>Faible</span><span>Moyen</span><span>Élevé</span>
          </div>
        </div>

        {/* Immunity */}
        <div className="card-surface rounded-2xl p-5">
          <h3 className="font-medium">Immunité</h3>
          <div className="mt-4 grid grid-cols-[auto_1fr] gap-4">
            <div>
              <div className="text-[10px] text-muted-foreground">Votre %,<br />Lympho. T (Total)</div>
              <div className="mt-2 text-2xl font-light">60-80 <span className="text-xs text-muted-foreground">%</span></div>
            </div>
            <ul className="space-y-1.5 text-xs">
              {[
                ["CD4+ T (auxiliaires)", "30-60"],
                ["CD8+ T (cytotoxiques)", "20-40"],
                ["Lympho. B", "5-15"],
                ["Monocytes", "2-10"],
              ].map(([l, v]) => (
                <li key={l} className="flex items-center justify-between rounded-full bg-white/5 px-3 py-1.5">
                  <span className="text-muted-foreground">{l}</span>
                  <span className="font-medium">{v} <span className="text-muted-foreground">%</span></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-3 space-y-1.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground"><span className="mr-2 inline-grid h-4 w-4 place-items-center rounded-full bg-primary/20 text-[10px] text-primary">1</span>Ratio CD4T/CD8T</span>
              <span className="text-accent">Sous-optimal · 1,4</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground"><span className="mr-2 inline-grid h-4 w-4 place-items-center rounded-full bg-primary/20 text-[10px] text-primary">2</span>Lympho./Monocyte</span>
              <span className="text-primary">Normal · 3,7</span>
            </div>
          </div>
        </div>

        {/* Inflammation */}
        <div className="card-surface rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Inflammation</h3>
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mt-1 text-xs text-muted-foreground">DNAm CRP (Protéine C-réactive)</div>
          <div className="mt-4 flex items-end gap-6">
            <div>
              <div className="text-[10px] text-muted-foreground">Score</div>
              <div className="text-2xl font-light">3,2 <span className="text-xs text-muted-foreground">mg/L</span></div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground">Percentile</div>
              <div className="text-2xl font-light">75<sup className="text-xs">e</sup></div>
            </div>
            <div className="ml-auto inline-flex items-center gap-1 text-xs text-primary"><ArrowUp className="h-3 w-3" />14,2%</div>
          </div>
          <div className="mt-3 h-2 w-full rounded-full bg-gradient-to-r from-primary via-[oklch(0.85_0.18_85)] to-destructive" />
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>0</span><span>40</span><span>80</span><span>100</span>
          </div>
          <p className="mt-3 rounded-lg bg-white/5 p-3 text-[11px] leading-relaxed">
            Le score est supérieur à <span className="font-medium">75 %</span> de la population de référence, indiquant une inflammation chronique <span className="font-medium">supérieure à la moyenne</span>.
          </p>
          <ul className="mt-3 space-y-1 text-[11px] text-muted-foreground">
            <li>▸ Risque accru de maladie cardiovasculaire et de syndrome métabolique.</li>
            <li>▸ Efficacité vaccinale diminuée.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
